import { Checkbox, Chip, FormControl, FormControlLabel, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, TextField } from "@mui/material";
import styles from "./addform.module.css";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import axios from 'axios';
import { toast } from 'react-smart-toaster';
import NumberFormat from "react-number-format";
import { forwardRef, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Box } from "@mui/system";
import { AddLogCallAPI } from "../../../components/AddLogs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import PhoneInput, { parsePhoneNumber, isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const schema = yup.object().shape({
    first_name: yup.string().required('This field is required').max(50, 'First name cannot be more than 50 characters'),
    last_name: yup.string().required('This field is required').max(50, 'Last name cannot be more than 50 characters'),
    login_method: yup.string().oneOf(['phone', 'email']).required('This field is required'),
    // Email is required when login_method is 'email', optional (but still validated for format if filled) when login_method is 'phone'
    email: yup.string().max(50, 'Email cannot be more than 50 characters')
        .when('login_method', {
            is: 'email',
            then: (schema) => schema.required('This field is required').email('Invalid E-mail'),
            otherwise: (schema) => schema.email('Invalid E-mail')
        }),
    // Phone is required when login_method is 'phone', optional (but still validated if filled) when login_method is 'email'
    phone: yup.string()
        .when('login_method', {
            is: 'phone',
            then: (schema) => schema.required('This field is required').test("valid-phone", "Invalid phone number", (value) => {
                if (!value) return false;
                return isValidPhoneNumber(value);
            }),
            otherwise: (schema) => schema.test("valid-phone", "Invalid phone number", (value) => {
                if (!value) return true;
                return isValidPhoneNumber(value);
            })
        }),
    property: yup.array().required('This field is required'),
    role: yup.string().required('This field is required'),
    is_hr_mode: yup.boolean(),
    checkin_reminder: yup.string(),
    checkout_reminder: yup.string(),
    lunch_time: yup.string(),
    checkin_time: yup.mixed().nullable().when('is_hr_mode', {
        is: true,
        then: (schema) => schema.required('This field is required'),
        otherwise: (schema) => schema.nullable()
    }),
    checkout_time: yup.mixed().nullable().when('is_hr_mode', {
        is: true,
        then: (schema) => schema.required('This field is required'),
        otherwise: (schema) => schema.nullable()
    }),
    wage_type: yup.string().when('is_hr_mode', {
        is: true,
        then: (schema) => schema.required('This field is required'),
        otherwise: (schema) => schema
    }),
    wage: yup.string().when('is_hr_mode', {
        is: true,
        then: (schema) => schema.required('This field is required'),
        otherwise: (schema) => schema
    }),
    ot_wage_type: yup.string().when('is_hr_mode', {
        is: true,
        then: (schema) => schema.required('This field is required'),
        otherwise: (schema) => schema
    }),
    ot_wage: yup.string().when('is_hr_mode', {
        is: true,
        then: (schema) => schema.required('This field is required'),
        otherwise: (schema) => schema
    }),
    working_days: yup.array().when('is_hr_mode', {
        is: true,
        then: (schema) => schema.min(1, 'Select at least one work day'),
        otherwise: (schema) => schema
    })
});

const PhoneTextField = forwardRef(
    ({ error, helperText, label, ...props }, ref) => (
        <TextField
            {...props}
            inputRef={ref}
            fullWidth
            label={label}
            variant="outlined"
        />
    )
);

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const UserAdd = ({ setAddModalOpen, setLoading, setSnackbarMsg, setSnackbarOpen, userAddLocal }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [roleList, setRoleList] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [selectPropertyIds, setSelectPropertyIds] = useState([]);
    const [propertyList, setPropertyList] = useState([]);
    const [isHrEnabled, setIsHrEnabled] = useState(false);
    const [loginMethod, setLoginMethod] = useState('phone');
    const [previewText, setPreviewText] = useState('');
    const [userData, setUserData] = useState({ name: '', time: null });
    const [wageAmount, setWageAmount] = useState({ normal: 0, ot: 0 });

    const { handleSubmit, control, errors, setValue, trigger } = useForm({
        resolver: yupResolver(schema), mode: 'onChange',
        defaultValues: {
            first_name: '',
            last_name: '',
            login_method: 'phone',
            email: '',
            phone: '',
            role: '',
            property: [],
            is_receive_emergency_call: false,
            is_receive_tenant_emergency_call: false,
            is_hr_mode: false,
            checkin_time: null,
            checkout_time: null,
            checkin_reminder: '',
            checkout_reminder: '',
            lunch_time: '',
            wage_type: 'hourly',
            ot_wage_type: 'hourly',
            wage: '',
            ot_wage: '',
            working_days: []
        }
    });

    useEffect(() => {
        const fetchRoles = () => {
            return axios.post('https://' + process.env.REACT_APP_AUTH0_DOMAIN + '/oauth/token', { "client_id": process.env.REACT_APP_AUTH0_AUDIENCE_CLIENT_ID, "client_secret": process.env.REACT_APP_AUTH0_AUDIENCE_CLIENT_SECRET, "audience": 'https://' + process.env.REACT_APP_AUTH0_DOMAIN + "/api/v2/", "grant_type": "client_credentials" }, {
                'access-control-allow-origin': '*',
            }).then(response => {
                let managementToken = response?.data?.access_token;

                axios.get('https://' + process.env.REACT_APP_AUTH0_DOMAIN + '/api/v2/roles', {
                    headers: {
                        Authorization: `Bearer ${managementToken}`,
                    }
                }).then(response2 => {
                    let roleListTemp = response2?.data?.filter(i => i?.name !== "Super Admin");
                    setRoleList(roleListTemp);
                }).catch(() => { });

            }).catch(() => { });
        }

        fetchRoles();
    }, []);

    useEffect(() => {
        const fetchProperties = async () => {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            await axios.get(process.env.REACT_APP_USER_API_URL + 'v1/get-all-properties', {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            }).then(response => {
                setPropertyList(response?.data?.data);
            }).catch(error => {
                setPropertyList([]);
            });
        }

        fetchProperties();
    }, [getAccessTokenSilently]);

    useEffect(() => {
        setPreviewText(`"Hi ${userData?.name || "User"} — your shift starts at ${userData?.time ? userData?.time : '09:00 AM'} today. Please use the management app to clock in when you arrive. — PropertyDek"`);
    }, [userData]);

    const onSubmit = async (data) => {
        let roleLabel = '';
        if (data?.role) {
            let sRole = roleList?.filter(i => i?.id === data?.role);
            if (sRole.length > 0) {
                roleLabel = sRole[0].name;
            }
        }
        data = { ...data, role_label: roleLabel };

        if (data?.is_receive_emergency_call) {
            data = { ...data, is_receive_emergency_call: 1 };
        } else {
            data = { ...data, is_receive_emergency_call: 0 };
        }

        if (data?.is_receive_tenant_emergency_call) {
            data = { ...data, is_receive_tenant_emergency_call: 1 };
        } else {
            data = { ...data, is_receive_tenant_emergency_call: 0 };
        }

        if (data?.is_hr_mode) {
            data = { ...data, is_hr_mode: 1, checkin_time: dayjs(data?.checkin_time).format('hh:mm A'), checkout_time: dayjs(data?.checkout_time).format('hh:mm A'), checkin_reminder: data?.checkin_reminder, checkout_reminder: data?.checkout_reminder, working_days: data?.working_days, lunch_time: data?.lunch_time };
        } else {
            data = { ...data, is_hr_mode: 0, checkin_time: '', checkout_time: '', checkin_reminder: '', checkout_reminder: '', working_days: [], lunch_time: '' };
        }

        if (data?.phone) {
            const phoneNumber = parsePhoneNumber(data?.phone);

            if (phoneNumber) {
                data = { ...data, country_code: phoneNumber?.country, isd_code: "+" + phoneNumber?.countryCallingCode, phone: phoneNumber?.nationalNumber };
            } else {
                data = { ...data, phone: data?.phone.replace(/[^0-9]/g, ""), country_code: "US", isd_code: "+1" };
            }
        }

        if (data?.wage) {
            data = { ...data, wage: data?.wage.replace(/,/g, "") };
        }

        if (data?.ot_wage) {
            data = { ...data, ot_wage: data?.ot_wage.replace(/,/g, "") };
        }

        setLoading(true);
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
        return axios.post(process.env.REACT_APP_USER_API_URL + 'v1/users-new-phone', data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then(response => {
            setAddModalOpen(false);
            setSnackbarOpen(true);
            setSnackbarMsg('A new user has been added successfully.');

            setLoading(false);

            userAddLocal(response.data.data);

            let respData = response.data.data;
            let logData = {
                'title': 'A new user has been added successfully.',
                'description': [
                    'Name: ' + respData.name,
                    'Login method: ' + (respData.login_method || 'phone'),
                    'Phone: ' + respData.phone,
                    'Email: ' + respData.email,
                    'Role: ' + respData.role
                ]
            }
            AddLogCallAPI(logData, token);
        }).catch(error => {
            if (typeof error.response !== 'undefined')
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    return (<div className={`${styles.modal}`}>
        <form onSubmit={handleSubmit(onSubmit)}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>

                <div className={`${styles.body}`}>

                    <div className={`${styles.panel}`}>

                        {/* Login method toggle */}
                        <div className={`${styles.hrToggleRow}`} style={{ marginBottom: 20 }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#534AB7" strokeWidth="1.8" aria-hidden="true">
                                <rect x="3" y="11" width="18" height="10" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                            <div className={`${styles.hrToggleText}`}>
                                <div className={`${styles.hrToggleTitle}`}>Login method</div>
                                <div className={`${styles.hrToggleSub}`}>Choose how this employee will sign in — phone OTP or email code. The other field becomes optional.</div>
                            </div>

                            <Controller
                                name="login_method"
                                control={control}
                                render={(field) => (
                                    <div className={styles.dayRow} style={{ marginBottom: 0, width: 180 }}>
                                        <button
                                            type="button"
                                            className={`${styles.dayChip} ${field.value === 'phone' ? styles.dayChipActive : ""}`}
                                            onClick={() => {
                                                field.onChange('phone');
                                                setLoginMethod('phone');
                                                trigger(['phone', 'email']);
                                            }}
                                        >
                                            Phone
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.dayChip} ${field.value === 'email' ? styles.dayChipActive : ""}`}
                                            onClick={() => {
                                                field.onChange('email');
                                                setLoginMethod('email');
                                                trigger(['phone', 'email']);
                                            }}
                                        >
                                            Email
                                        </button>
                                    </div>
                                )}
                            />
                        </div>

                        <div className={`${styles.formGrid}`}>
                            <div className={`${styles.fieldGroup}`}>
                                <div className="FormGroup">
                                    <Controller
                                        name={`first_name`}
                                        control={control}
                                        render={(field) => (
                                            <TextField
                                                onChange={(e) => {
                                                    field.onChange(e);

                                                    setUserData(prev => {
                                                        return { ...prev, name: e.target.value };
                                                    });
                                                }}
                                                label="First Name*"
                                                value={field.value}
                                            />
                                        )}
                                    />
                                    {errors?.first_name && (<p className={`${styles.ErrorM}`}>{errors?.first_name?.message}</p>)}
                                </div>
                            </div>
                            <div className={`${styles.fieldGroup}`}>
                                <div className="FormGroup">
                                    <Controller
                                        name={`last_name`}
                                        control={control}
                                        render={(field) => (
                                            <TextField
                                                onChange={(e) => field.onChange(e)}
                                                label="Last Name*"
                                                value={field.value}
                                            />
                                        )}
                                    />
                                    {errors?.last_name && (<p className={`${styles.ErrorM}`}>{errors?.last_name?.message}</p>)}
                                </div>
                            </div>
                            <div className={`${styles.fieldGroup}`}>
                                <div className="FormGroup">
                                    <Controller
                                        name={`phone`}
                                        control={control}
                                        render={(field) => (
                                            <PhoneInput
                                                international
                                                countryCallingCodeEditable={false}
                                                defaultCountry="US"
                                                value={field?.value}
                                                onChange={(e) => field.onChange(e)}
                                                inputComponent={PhoneTextField}
                                                label={loginMethod === 'phone' ? 'Phone (SMS clock-in)*' : 'Phone (SMS clock-in)'}
                                            />)}
                                    />
                                    {errors?.phone && (<p className={`${styles.ErrorM}`}>{errors?.phone?.message}</p>)}
                                </div>
                            </div>
                            <div className={`${styles.fieldGroup}`}>
                                <div className="FormGroup">
                                    <Controller
                                        name={`email`}
                                        control={control}
                                        render={(field) => (
                                            <TextField
                                                onChange={(e) => field.onChange(e)}
                                                label={loginMethod === 'email' ? "Email*" : "Email"}
                                                value={field.value}
                                            />
                                        )}
                                    />
                                    {errors?.email && (<p className={`${styles.ErrorM}`}>{errors?.email?.message}</p>)}
                                </div>
                            </div>
                            <div className={`${styles.fieldGroup}`}>
                                <div className="FormGroup">
                                    <FormControl fullWidth>
                                        <InputLabel id="demo-simple-select-label">Role*</InputLabel>
                                        <Controller
                                            name="role"
                                            control={control}
                                            render={(field) => (
                                                <Select
                                                    {...field}
                                                    label="Role*"
                                                    onChange={(e) => {
                                                        field.onChange(e);

                                                        let sRole = roleList?.filter(i => i?.id === e.target.value);
                                                        if (sRole.length > 0) {
                                                            if (sRole[0].name === 'Admin') {
                                                                setIsAdmin(true);
                                                                setValue('property', ['All']);
                                                            } else {
                                                                setIsAdmin(false);
                                                            }
                                                        } else {
                                                            setIsAdmin(false);
                                                        }
                                                    }}
                                                >
                                                    {roleList?.map(item => {
                                                        return (<MenuItem key={item?.id} value={item?.id}>{item?.name}</MenuItem>)
                                                    })}
                                                </Select>
                                            )}
                                        />
                                    </FormControl>
                                    {errors?.role && (<p className={`${styles.ErrorM}`}>{errors?.role.message}</p>)}
                                </div>
                            </div>
                            <div className={`${styles.fieldGroup}`}>
                                <div className="FormGroup">
                                    <FormControl fullWidth>
                                        <InputLabel id="demo-simple-select-label">Property*</InputLabel>
                                        <Controller
                                            name="property"
                                            control={control}
                                            render={(field) => (
                                                <Select
                                                    {...field}
                                                    labelId="demo-simple-select-label"
                                                    id="demo-simple-select"
                                                    label="Property*"
                                                    multiple
                                                    onChange={(e) => {
                                                        field.onChange(e);

                                                        if (isAdmin) {
                                                            setSelectPropertyIds(e.target.value);
                                                        } else {
                                                            if (e.target.value?.indexOf('All') > -1) {
                                                                if (selectPropertyIds.length === propertyList.length) {
                                                                    setValue('property', []);
                                                                    setSelectPropertyIds([]);
                                                                } else {
                                                                    let allP = propertyList?.map(i => i?.value);
                                                                    setValue('property', allP);
                                                                    setSelectPropertyIds(allP);
                                                                }
                                                            } else {
                                                                setSelectPropertyIds(e.target.value);
                                                            }
                                                        }
                                                    }}
                                                    input={<OutlinedInput label="Property*" />}
                                                    renderValue={(selected) => {
                                                        return (isAdmin ? <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                            <Chip label={"All Properties"} />
                                                        </Box> : <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                            {propertyList?.map(item => {
                                                                return (selected?.indexOf(item?.value) > -1) ? (<Chip key={item?.value} label={item?.label} />) : null;
                                                            })}
                                                        </Box>)
                                                    }}
                                                    MenuProps={MenuProps}
                                                    disabled={isAdmin}
                                                >
                                                    <MenuItem value="All">
                                                        <Checkbox
                                                            checked={propertyList.length > 0 && selectPropertyIds.length === propertyList.length}
                                                            indeterminate={selectPropertyIds.length > 0 && selectPropertyIds.length < propertyList.length}
                                                        />
                                                        <ListItemText primary="All Properties" />
                                                    </MenuItem>
                                                    {propertyList?.map(item => {
                                                        return (<MenuItem key={item?.value} value={item?.value}>
                                                            <Checkbox checked={selectPropertyIds.indexOf(item?.value) > -1} />
                                                            <ListItemText primary={item?.label} />
                                                        </MenuItem>)
                                                    })}
                                                </Select>
                                            )}
                                        />
                                    </FormControl>
                                    {errors?.property && (<p className={`${styles.ErrorM}`}>{errors?.property.message}</p>)}
                                </div>
                            </div>
                        </div>
                        <div>
                            <Controller
                                name="is_receive_emergency_call"
                                control={control}
                                render={(field) => (<FormControlLabel
                                    control={
                                        <Checkbox
                                            {...field}
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        />
                                    }
                                    label="Can receive emergency calls from management app."
                                    className="CustomCheckLabel"
                                />)}
                            />
                        </div>
                        <div>
                            <Controller
                                name="is_receive_tenant_emergency_call"
                                control={control}
                                render={(field) => (<FormControlLabel
                                    control={
                                        <Checkbox
                                            {...field}
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        />
                                    }
                                    label="Can receive emergency calls from tenants."
                                    className="CustomCheckLabel"
                                />)}
                            />
                        </div>
                    </div>

                    <div className={`${styles.panel}`}>
                        <div className={`${styles.hrToggleRow}`}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#534AB7" strokeWidth="1.8" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                            </svg>
                            <div className={`${styles.hrToggleText}`}>
                                <div className={`${styles.hrToggleTitle}`}>Enable HR mode</div>
                                <div className={`${styles.hrToggleSub}`}>Enables SMS clock-in/out and shift reminders</div>
                            </div>

                            <Controller
                                name="is_hr_mode"
                                control={control}
                                render={(field) => (
                                    <button
                                        type="button"
                                        className={`${styles.toggle} ${field?.value ? styles.toggleOn : ""}`}
                                        aria-label="Toggle HR mode"
                                        aria-pressed={field?.value}
                                        onClick={() => {
                                            field.onChange(!field.value);

                                            setIsHrEnabled(!field.value);
                                        }}
                                    >
                                        <div className={styles.toggleThumb} />
                                    </button>
                                )}
                            />
                        </div>

                        {isHrEnabled && (<>
                            <div className={`${styles.sectionLabel}`} style={{ marginTop: 20 }}>Work schedule</div>
                            <Controller
                                name="working_days"
                                control={control}
                                render={(field) => (<>
                                    <div className={styles.dayRow}>
                                        {DAYS.map((day, index) => {
                                            const selected = field.value.includes(day);

                                            return (<button key={day} type="button" className={`${styles.dayChip} ${selected ? styles.dayChipActive : ""}`}
                                                onClick={() => {
                                                    let updatedDays;
                                                    if (selected) {
                                                        updatedDays = field.value.filter((i) => i !== day);
                                                    } else {
                                                        updatedDays = [...field.value, day];
                                                    }

                                                    field.onChange(updatedDays);
                                                }}
                                                aria-pressed={selected}
                                            >
                                                {day}
                                            </button>
                                            );
                                        })}
                                    </div>

                                    {errors?.working_days && (<p className={styles.ErrorM}>{errors.working_days.message}</p>)}
                                </>)}
                            />

                            <div className={`${styles.timeGrid}`}>
                                <div className={`${styles.wrapper} FormGroup`}>
                                    <span className={`${styles.label}`}>Shift start</span>
                                    <Controller
                                        name="checkin_time"
                                        control={control}
                                        render={(field) => (
                                            <TimePicker
                                                value={field.value}
                                                onChange={(value) => {
                                                    field.onChange(value);

                                                    setUserData(prev => {
                                                        return { ...prev, time: dayjs(value).format('hh:mm A') };
                                                    });
                                                }}
                                                renderInput={(params) => (<TextField {...params} fullWidth />)}
                                            />
                                        )}
                                    />
                                    {errors?.checkin_time && (<p className={`${styles.ErrorM}`}>{errors?.checkin_time.message}</p>)}
                                </div>
                                <div className={`${styles.wrapper} FormGroup`}>
                                    <span className={`${styles.label}`}>Shift end</span>
                                    <Controller
                                        name="checkout_time"
                                        control={control}
                                        render={(field) => (
                                            <TimePicker
                                                value={field.value}
                                                onChange={(value) => field.onChange(value)}
                                                renderInput={(params) => (<TextField {...params} fullWidth />)}
                                            />
                                        )}
                                    />
                                    {errors?.checkout_time && (<p className={`${styles.ErrorM}`}>{errors?.checkout_time.message}</p>)}
                                </div>
                            </div>

                            <div className={`${styles.sectionLabel}`} style={{ marginTop: 20 }}>SMS reminders</div>
                            <div className={`${styles.smsGrid}`}>
                                <div className={`${styles.fieldGroup}`}>
                                    <div className="FormGroup">
                                        <FormControl fullWidth>
                                            <InputLabel id="demo-simple-select-label">Clock-in reminder</InputLabel>
                                            <Controller
                                                name="checkin_reminder"
                                                control={control}
                                                render={(field) => (<Select
                                                    {...field}
                                                    label="Clock-in reminder"
                                                >
                                                    <MenuItem value={'5 minutes'}>5 min before shift</MenuItem>
                                                    <MenuItem value={'15 minutes'}>15 min before shift</MenuItem>
                                                    <MenuItem value={'30 minutes'}>30 min before shift</MenuItem>
                                                    <MenuItem value={'1 hour'}>1 hr before shift</MenuItem>
                                                </Select>)}
                                            />
                                        </FormControl>
                                    </div>
                                </div>
                                <div className={`${styles.fieldGroup}`}>
                                    <div className="FormGroup">
                                        <FormControl fullWidth>
                                            <InputLabel id="demo-simple-select-label">Missed clock-out alert</InputLabel>
                                            <Controller
                                                name="checkout_reminder"
                                                control={control}
                                                render={(field) => (<Select
                                                    {...field}
                                                    label="Missed clock-out alert"
                                                >
                                                    <MenuItem value={'30 minutes'}>30 min after shift end</MenuItem>
                                                    <MenuItem value={'1 hour'}>1 hr after shift end</MenuItem>
                                                    <MenuItem value={'2 hours'}>2 hrs after shift end</MenuItem>
                                                </Select>)}
                                            />
                                        </FormControl>
                                    </div>
                                </div>
                                <div className={`${styles.fieldGroup}`}>
                                    <div className="FormGroup">
                                        <FormControl fullWidth>
                                            <InputLabel id="demo-simple-select-label">Lunch Break</InputLabel>
                                            <Controller
                                                name="lunch_time"
                                                control={control}
                                                render={(field) => (<Select
                                                    {...field}
                                                    label="Lunch Break"
                                                >
                                                    <MenuItem value={15}>15 Min</MenuItem>
                                                    <MenuItem value={30}>30 Min</MenuItem>
                                                    <MenuItem value={45}>45 Min</MenuItem>
                                                    <MenuItem value={60}>1 Hour</MenuItem>
                                                </Select>)}
                                            />
                                        </FormControl>
                                    </div>
                                </div>
                            </div>

                            <div className={`${styles.fieldGroup} ${styles.fieldGroupFull}`}>
                                <div className={`${styles.sectionLabel}`} style={{ marginTop: 8, marginBottom: 10 }}>Compensation</div>
                                <div className={`${styles.wageSubLabel}`}>Normal wage</div>
                                <div className={`${styles.wageRow}`}>
                                    <div className="FormGroup" style={{ flex: "0 0 140px" }}>
                                        <FormControl fullWidth>
                                            <InputLabel>Wage type</InputLabel>
                                            <Controller
                                                name="wage_type"
                                                control={control}
                                                render={(field) => (<Select
                                                    {...field}
                                                    label="Wage type"
                                                >
                                                    <MenuItem value={"hourly"}>Hourly</MenuItem>
                                                </Select>)}
                                            />
                                        </FormControl>
                                        {errors?.wage_type && (<p className={`${styles.ErrorM}`}>{errors?.wage_type?.message}</p>)}
                                    </div>
                                    <div className="FormGroup" style={{ flex: 1 }}>
                                        <Controller
                                            name={`wage`}
                                            control={control}
                                            render={(field) => (
                                                <NumberFormat
                                                    {...field}
                                                    customInput={TextField}
                                                    fullWidth
                                                    label={`Amount (/ hr)`}
                                                    placeholder="0.00"
                                                    InputProps={{
                                                        startAdornment: <span style={{ marginRight: 0, color: "#888", marginLeft: 12 }}>$</span>
                                                    }}
                                                    thousandSeparator={true}
                                                    allowNegative={false}
                                                    decimalScale={2}
                                                    fixedDecimalScale
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                        setWageAmount(prev => {
                                                            return { ...prev, normal: (e?.target?.value).replace(/,/g, "") }
                                                        })
                                                    }}
                                                />
                                            )}
                                        />
                                        {errors?.wage && (<p className={`${styles.ErrorM}`}>{errors?.wage?.message}</p>)}
                                    </div>
                                </div>
                                {/* OT wage row */}
                                <div className={`${styles.wageSubLabel}`} style={{ marginTop: 12 }}>Overtime (OT) wage</div>
                                <div className={`${styles.wageRow}`}>
                                    <div className="FormGroup" style={{ flex: "0 0 140px" }}>
                                        <FormControl fullWidth>
                                            <InputLabel>Wage type</InputLabel>
                                            <Controller
                                                name="ot_wage_type"
                                                control={control}
                                                render={(field) => (<Select
                                                    {...field}
                                                    label="OT wage type"
                                                >
                                                    <MenuItem value={"hourly"}>Hourly</MenuItem>
                                                </Select>)}
                                            />
                                            {errors?.ot_wage_type && (<p className={`${styles.ErrorM}`}>{errors?.ot_wage_type?.message}</p>)}
                                        </FormControl>
                                    </div>
                                    <div className="FormGroup" style={{ flex: 1 }}>
                                        <Controller
                                            name={`ot_wage`}
                                            control={control}
                                            render={(field) => (
                                                <NumberFormat
                                                    {...field}
                                                    customInput={TextField}
                                                    fullWidth
                                                    label={`OT amount (/ hr)`}
                                                    placeholder="0.00"
                                                    InputProps={{
                                                        startAdornment: <span style={{ marginRight: 0, color: "#888", marginLeft: 12 }}>$</span>
                                                    }}
                                                    thousandSeparator={true}
                                                    allowNegative={false}
                                                    decimalScale={2}
                                                    fixedDecimalScale
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                        setWageAmount(prev => {
                                                            return { ...prev, ot: (e?.target?.value).replace(/,/g, "") }
                                                        })
                                                    }}
                                                />
                                            )}
                                        />
                                        {errors?.ot_wage && (<p className={`${styles.ErrorM}`}>{errors?.ot_wage?.message}</p>)}
                                    </div>
                                </div>
                            </div>

                            {(wageAmount.normal > 0 || wageAmount.ot > 0) && (<div className={`${styles.wageSummaryWrap}`}>
                                {wageAmount.normal > 0 && (<div className={`${styles.wageSummaryCard}`}>
                                    <div className={`${styles.wageSummaryIcon}`}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                            <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                                        </svg>
                                    </div>
                                    <div className={`${styles.wageSummaryInfo}`}>
                                        <div className={`${styles.wageSummaryLabel}`}>Normal wage</div>
                                        <div className={`${styles.wageSummaryValue}`}>
                                            ${parseFloat(wageAmount.normal || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            <span className={`${styles.wageSummaryUnit}`}>/ hr</span>
                                        </div>
                                    </div>
                                    <div className={`${styles.wageSummaryType}`}>Hourly</div>
                                </div>)}

                                {wageAmount.ot > 0 && (<div className={`${styles.wageSummaryCard} ${styles.wageSummaryCardOt}`}>
                                    <div className={`${styles.wageSummaryIcon} ${styles.wageSummaryIconOt}`}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                                        </svg>
                                    </div>
                                    <div className={`${styles.wageSummaryInfo}`}>
                                        <div className={`${styles.wageSummaryLabel} ${styles.wageSummaryLabelOt}`}>Overtime (OT) wage</div>
                                        <div className={`${styles.wageSummaryValue} ${styles.wageSummaryValueOt}`}>
                                            ${parseFloat(wageAmount.ot || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            <span className={`${styles.wageSummaryUnit} ${styles.wageSummaryUnitOt}`}>/ hr</span>
                                        </div>
                                    </div>
                                    <div className={`${styles.wageSummaryType} ${styles.wageSummaryTypeOt}`}>Hourly</div>
                                </div>)}

                            </div>)}

                            <div className={`${styles.reminderPreview}`}>
                                <div className={`${styles.previewLabel}`}>Reminder preview</div>
                                <div className={`${styles.previewText}`}>{previewText}</div>
                            </div>
                        </>)}
                    </div>
                </div>
            </LocalizationProvider>

            {/* Footer */}
            <div className={`${styles.footer}`}>
                <div className={`${styles.footerSpacer}`} />
                <button className={`${styles.cancelBtn}`} type="button" onClick={(e) => setAddModalOpen(false)}>Cancel</button>
                <button className={`${styles.saveBtn}`} type="submit">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Create employee
                </button>
            </div>
        </form>
    </div>);
}

export default UserAdd;